/**
 * SubmitPage — Cohort Upload Portal
 * ==================================
 * Standalone page at /submit?cohort=<slug>
 *
 * Handles four auth states:
 *   1. Logged in + already a cohort member  → show upload form
 *   2. Logged in + NOT a member             → join button, then upload form
 *   3. Not logged in + has an account        → login link, then join + upload
 *   4. Not logged in + no account            → register link, then join + upload
 *
 * After upload: shows a mini map preview of the project in its zone.
 *
 * Place at: frontend/src/pages/SubmitPage.jsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Upload, Check, ChevronDown, Sparkles, LogIn, UserPlus,
  FileText, Image, X, Loader2, ExternalLink, Tag,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

// ── Zone auto-detect from file extension ───────────────────────────────
const EXT_TO_ZONE = {
  // Build Something
  dwg: 'build', rvt: 'build', ifc: 'build', step: 'build', stp: 'build',
  iges: 'build', stl: 'build', '3mf': 'build', gcode: 'build',
  // Make It Beautiful
  psd: 'beautiful', ai: 'beautiful', sketch: 'beautiful', fig: 'beautiful',
  xd: 'beautiful', indd: 'beautiful', svg: 'beautiful',
  // Make It Sound Good
  mp3: 'sound', wav: 'sound', aiff: 'sound', flac: 'sound',
  mid: 'sound', midi: 'sound', aup3: 'sound',
  // Make It Taste Good (recipes are usually docs/images — no unique extensions)
  // Spin A Tale
  fdx: 'story', fountain: 'story', srt: 'story', vtt: 'story',
  // Patch People Up (medical data — no unique common extensions)
  // Make It Smart
  py: 'smart', js: 'smart', jsx: 'smart', ts: 'smart', tsx: 'smart',
  java: 'smart', cpp: 'smart', c: 'smart', rs: 'smart', go: 'smart',
  ipynb: 'smart', sql: 'smart',
  // Sell It (business docs are usually .pdf/.docx — no unique extensions)
};

function detectZoneFromFiles(files) {
  for (const file of files) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext && EXT_TO_ZONE[ext]) return EXT_TO_ZONE[ext];
  }
  return null;
}

// ── Zone labels + colors for the badge ─────────────────────────────────
const ZONE_META = {
  build:     { label: 'Build Something',      color: '#f59e0b' },
  beautiful: { label: 'Make It Beautiful',     color: '#ec4899' },
  sound:     { label: 'Make It Sound Good',    color: '#06b6d4' },
  taste:     { label: 'Make It Taste Good',    color: '#f97316' },
  story:     { label: 'Spin A Tale',           color: '#8b5cf6' },
  heal:      { label: 'Patch People Up',       color: '#ef4444' },
  smart:     { label: 'Make It Smart',         color: '#6366f1' },
  sell:      { label: 'Sell It',               color: '#22c55e' },
};


// ========================================================================
// MAIN COMPONENT
// ========================================================================

export default function SubmitPage() {
  const [searchParams] = useSearchParams();
  const cohortSlug = searchParams.get('cohort');

  // ── Join code ──────────────────────────────────────────────────────
  // Arrives in the share link: /submit?cohort=<slug>&code=<code>
  //
  // Before join codes existed, the slug alone was enough to enrol in a
  // PRIVATE class — and slugs are generated from the class name, so
  // "Film Test 001" was "film-test-001". Anyone signed in who guessed it
  // became a member, and membership grants file access, search results and
  // the cohort map.
  //
  // The URL is a convenience, not the only route: a student who was given
  // the code verbally can type it below. Anyone arriving without one gets
  // the input rather than a dead end.
  const urlJoinCode = searchParams.get('code') || '';

  // Where to send someone back to after login or registration. The code
  // MUST survive that round trip: a student who clicks the share link,
  // creates an account, and returns to a bare /submit?cohort=... would be
  // asked for a code they were never shown, having done nothing wrong.
  const submitReturnPath = urlJoinCode
    ? `/submit?cohort=${cohortSlug}&code=${encodeURIComponent(urlJoinCode)}`
    : `/submit?cohort=${cohortSlug}`;
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  // Cohort state
  const [cohort, setCohort] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(true);
  const [cohortError, setCohortError] = useState(null);

  // Membership state
  const [isMember, setIsMember] = useState(false);
  const [joining, setJoining] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(null);

  // Prefill from the URL once the param is known. Uppercased because the
  // backend compares case-insensitively but the field should LOOK like the
  // code on the whiteboard.
  useEffect(() => {
    if (urlJoinCode) setCodeInput(urlJoinCode.trim().toUpperCase());
  }, [urlJoinCode]);

  // Form state
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [projectType, setProjectType] = useState('digital');
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [customLabel, setCustomLabel] = useState('');
  const [detectedZone, setDetectedZone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Post-submit state
  const [submitted, setSubmitted] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);

  // ── Fetch cohort info (public, no auth) ────────────────────────────
  useEffect(() => {
    if (!cohortSlug) {
      setCohortError('No cohort specified. Use a link from your instructor.');
      setCohortLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/cohorts/${cohortSlug}`);
        setCohort(res.data);
      } catch {
        setCohortError('Cohort not found. Check the link from your instructor.');
      } finally {
        setCohortLoading(false);
      }
    })();
  }, [cohortSlug]);

  // ── Check membership when user is authenticated ────────────────────
  useEffect(() => {
    if (!isAuthenticated || !cohort) return;
    (async () => {
      try {
        const res = await api.get(`/cohorts/${cohort.slug}/members`);
        const members = res.data?.members || [];
        const me = members.find(m => m.userId === user?.id);
        setIsMember(!!me);
      } catch {
        setIsMember(false);
      }
    })();
  }, [isAuthenticated, cohort, user]);

  // ── Join cohort ────────────────────────────────────────────────────
  const handleJoin = async () => {
    setJoining(true);
    setCodeError(null);
    try {
      await api.post(`/cohorts/${cohort.slug}/join`, {
        joinCode: codeInput.trim(),
      });
      setIsMember(true);
      toast.success('Joined the cohort!');
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'join_code_required') {
        // Distinct from a generic failure so the form can point at the
        // field instead of showing an unexplained red toast.
        setCodeError(
          codeInput.trim()
            ? 'That code is not right. Check it with your instructor.'
            : 'This class needs a join code.'
        );
      } else {
        toast.error(data.error || 'Failed to join');
      }
    } finally {
      setJoining(false);
    }
  };

  // ── File handling ──────────────────────────────────────────────────
  const handleFilesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
    // Auto-detect zone
    const zone = detectZoneFromFiles(newFiles);
    if (zone && !detectedZone) setDetectedZone(zone);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCoverChange = (e) => {
    if (e.target.files?.[0]) setCoverImage(e.target.files[0]);
  };

  // ── Label toggle ───────────────────────────────────────────────────
  const toggleLabel = (label) => {
    setSelectedLabels(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  const addCustomLabel = () => {
    const trimmed = customLabel.trim();
    if (trimmed && !selectedLabels.includes(trimmed)) {
      setSelectedLabels(prev => [...prev, trimmed]);
      setCustomLabel('');
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (files.length === 0) {
      toast.error('Please upload at least one file');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Create the project
      const formData = new FormData();
      formData.append('name', projectName.trim());
      formData.append('description', description.trim());
      formData.append('project_type', projectType);
      if (detectedZone) formData.append('category', detectedZone);
      if (coverImage) formData.append('cover_image', coverImage);
      files.forEach(f => formData.append('files', f));

      const projectRes = await api.post('/projects', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const project = projectRes.data;
      const projectId = project.id || project.project?.id;

      // 2. Add project to cohort with labels
      await api.post(`/cohorts/${cohort.slug}/projects`, {
        projectId,
        labels: selectedLabels,
      });

      setCreatedProject({ ...project, id: projectId });
      setSubmitted(true);
      toast.success('Project submitted!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit project');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / error states ─────────────────────────────────────────
  if (cohortLoading || authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (cohortError) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md text-center">
          <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Oops</h1>
          <p className="text-zinc-400 mb-6">{cohortError}</p>
          <Link to="/" className="text-purple-400 hover:text-purple-300 text-sm">
            Go to Enumé home
          </Link>
        </div>
      </div>
    );
  }

  // ── Post-submit success ────────────────────────────────────────────
  if (submitted && createdProject) {
    const zone = detectedZone ? ZONE_META[detectedZone] : null;
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-lg w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Project Submitted!</h1>
          <p className="text-zinc-400 mb-4">
            Your project is now part of <span className="text-white font-medium">{cohort.name}</span>
          </p>
          {zone && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                 style={{ backgroundColor: zone.color + '20', color: zone.color }}>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: zone.color }} />
              <span className="text-sm font-medium">{zone.label}</span>
            </div>
          )}
          <div className="flex flex-col gap-3 mt-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              View on the Map
            </button>
            <button
              onClick={() => {
                setSubmitted(false);
                setCreatedProject(null);
                setProjectName('');
                setDescription('');
                setFiles([]);
                setCoverImage(null);
                setSelectedLabels([]);
                setDetectedZone(null);
              }}
              className="px-4 py-3 text-zinc-400 hover:text-white transition-colors"
            >
              Submit Another Project
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Not authenticated ──────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center">
          <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">{cohort.name}</h1>
          {cohort.description && (
            <p className="text-zinc-400 mb-6 text-sm">{cohort.description}</p>
          )}
          <p className="text-zinc-300 mb-6">Sign in to submit your project to this cohort.</p>
          <div className="flex flex-col gap-3">
            <Link
              to={`/login?redirect=${encodeURIComponent(submitReturnPath)}`}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition-colors"
            >
              <LogIn className="w-4 h-4" />
              Sign In
            </Link>
            <Link
              to={`/register?redirect=${encodeURIComponent(submitReturnPath)}`}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Authenticated but not a member ─────────────────────────────────
  if (!isMember) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center">
          <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">{cohort.name}</h1>
          {cohort.description && (
            <p className="text-zinc-400 mb-6 text-sm">{cohort.description}</p>
          )}
          <p className="text-zinc-300 mb-6">Join this cohort to start submitting projects.</p>

          {/* Code field. Shown whenever the class requires one — including
              when it arrived in the URL, so the student can SEE what is
              being submitted rather than trusting an invisible parameter. */}
          {cohort.requiresJoinCode && (
            <div className="mb-5 text-left">
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Join code
              </label>
              <input
                type="text"
                value={codeInput}
                onChange={e => { setCodeInput(e.target.value.toUpperCase()); setCodeError(null); }}
                onKeyDown={e => { if (e.key === 'Enter' && !joining) handleJoin(); }}
                placeholder="ABCD123456"
                maxLength={16}
                autoComplete="off"
                spellCheck={false}
                className={`w-full px-4 py-3 bg-zinc-900 border rounded-xl text-white text-center text-lg tracking-[0.2em] font-mono placeholder-zinc-600 focus:outline-none focus:ring-2 ${
                  codeError
                    ? 'border-red-500/60 focus:ring-red-500/40'
                    : 'border-zinc-700 focus:ring-purple-500/50 focus:border-purple-500/50'
                }`}
              />
              {codeError ? (
                <p className="text-xs text-red-400 mt-2">{codeError}</p>
              ) : (
                <p className="text-xs text-zinc-500 mt-2">
                  From your instructor. Case does not matter.
                </p>
              )}
            </div>
          )}

          <button
            onClick={handleJoin}
            disabled={joining || (cohort.requiresJoinCode && !codeInput.trim())}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 text-white rounded-xl transition-colors"
          >
            {joining ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {joining ? 'Joining...' : 'Join Cohort'}
          </button>
        </div>
      </div>
    );
  }

  // ── Upload form (authenticated + member) ───────────────────────────
  const taxonomy = cohort.taxonomy || {};
  const taxonomyEnabled = taxonomy.enabled;
  const adminLabels = taxonomy.labels || [];
  const allowStudentAdditions = taxonomy.allow_student_additions !== false;

  return (
    <div className="min-h-screen bg-zinc-950 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white mb-1">Submit to {cohort.name}</h1>
          <p className="text-zinc-400 text-sm">Upload your project files and they will appear on the cosmic map.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              placeholder="What did you make?"
              maxLength={200}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Tell us about your project..."
              rows={3}
              maxLength={2000}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 resize-none"
            />
          </div>

          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Cover Image (optional)</label>
            <label className="flex items-center gap-3 px-4 py-3 bg-zinc-900 border border-zinc-700 border-dashed rounded-xl cursor-pointer hover:border-zinc-600 transition-colors">
              <Image className="w-5 h-5 text-zinc-500" />
              <span className="text-zinc-400 text-sm">
                {coverImage ? coverImage.name : 'Choose a cover image...'}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverChange}
                className="hidden"
              />
            </label>
          </div>

          {/* Project Files */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Project Files</label>
            <label className="flex items-center gap-3 px-4 py-3 bg-zinc-900 border border-zinc-700 border-dashed rounded-xl cursor-pointer hover:border-zinc-600 transition-colors">
              <Upload className="w-5 h-5 text-zinc-500" />
              <span className="text-zinc-400 text-sm">
                Upload files (.dwg, .rvt, .psd, .blend, .py, etc.)
              </span>
              <input
                type="file"
                multiple
                onChange={handleFilesChange}
                className="hidden"
              />
            </label>
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 bg-zinc-900/50 rounded-lg">
                    <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    <span className="text-sm text-zinc-300 truncate flex-1">{file.name}</span>
                    <span className="text-xs text-zinc-500">{(file.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-zinc-500 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {detectedZone && ZONE_META[detectedZone] && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-zinc-500">Auto-detected zone:</span>
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: ZONE_META[detectedZone].color + '20',
                    color: ZONE_META[detectedZone].color,
                  }}
                >
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ZONE_META[detectedZone].color }} />
                  {ZONE_META[detectedZone].label}
                </span>
              </div>
            )}
          </div>

          {/* Project Type */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Project Type</label>
            <div className="flex gap-3">
              {[
                { value: 'physical', label: 'Physical', desc: 'Tangible object' },
                { value: 'digital',  label: 'Digital',  desc: 'Software / media' },
                { value: 'hybrid',   label: 'Hybrid',   desc: 'Both' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setProjectType(opt.value)}
                  className={`flex-1 px-3 py-3 rounded-xl border text-center transition-all ${
                    projectType === opt.value
                      ? 'border-purple-500 bg-purple-500/10 text-white'
                      : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-xs opacity-60 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Taxonomy Labels */}
          {taxonomyEnabled && adminLabels.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Tag className="w-4 h-4 inline mr-1.5" />
                Category Labels
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {adminLabels.map(label => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleLabel(label)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      selectedLabels.includes(label)
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {allowStudentAdditions && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customLabel}
                    onChange={e => setCustomLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomLabel(); } }}
                    placeholder="Add your own label..."
                    maxLength={100}
                    className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                  />
                  <button
                    type="button"
                    onClick={addCustomLabel}
                    className="px-3 py-2 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 rounded-lg text-sm transition-colors"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !projectName.trim() || files.length === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 disabled:from-zinc-700 disabled:to-zinc-700 disabled:text-zinc-500 text-white font-medium rounded-xl transition-all"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Submit Project
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}